function initHeaderNavPanel() {
  if (window.csHeaderNavPanelState) return;

  const OPEN_CLASS = 'is-open';
  const BODY_CLASS = 'nav-panel-open';
  const VIEW_CLASSES = ['is-view-forward', 'is-view-back'];
  const HOVER_CLOSE_DELAY = 400;
  const HOVER_OPEN_DELAY = 150;
  const DROPDOWN_TRIGGER_SELECTOR = '.site-nav__link--has-dropdown[data-nav-panel-trigger]';
  const TRIGGER_LABEL_CLASS = 'nav-panel-trigger__label';
  const TRIGGER_OPEN_CLASS = 'nav-panel-trigger--open';
  let lastFocused = null;
  let current = null;
  let currentTrigger = null;
  let hoverTimer = null;
  let openTimer = null;
  let ariaExpandedObserver = null;
  let drilldownOrigin = null;

  const desktop = window.matchMedia('(min-width: 769px)');

  function wrapTriggerLabel(trigger) {
    if (trigger.querySelector(`:scope > .${TRIGGER_LABEL_CLASS}`)) return;
    const textNode = Array.from(trigger.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0
    );
    if (!textNode) return;
    const label = document.createElement('span');
    label.className = TRIGGER_LABEL_CLASS;
    label.textContent = textNode.textContent.trim();
    trigger.replaceChild(label, textNode);
  }

  function prepareTriggerLabels() {
    document.querySelectorAll(DROPDOWN_TRIGGER_SELECTOR).forEach(wrapTriggerLabel);
  }

  function syncTriggerStates() {
    document.querySelectorAll(DROPDOWN_TRIGGER_SELECTOR).forEach((trigger) => {
      const isActiveTrigger = trigger === currentTrigger;
      trigger.classList.toggle(TRIGGER_OPEN_CLASS, isActiveTrigger);
      trigger.setAttribute('aria-expanded', isActiveTrigger ? 'true' : 'false');
    });
  }

  function triggerStateHasDrifted() {
    return Array.from(document.querySelectorAll(DROPDOWN_TRIGGER_SELECTOR)).some((trigger) => {
      const expected = trigger === currentTrigger ? 'true' : 'false';
      return trigger.getAttribute('aria-expanded') !== expected;
    });
  }

  function watchVendorAriaExpandedStomps() {
    const observer = new MutationObserver(() => {
      if (triggerStateHasDrifted()) syncTriggerStates();
    });
    document.querySelectorAll(DROPDOWN_TRIGGER_SELECTOR).forEach((trigger) => {
      observer.observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
    return observer;
  }

  function panelFor(trigger) {
    const panels = Array.from(document.querySelectorAll('[data-nav-panel]'));
    if (!panels.length) return null;
    const id = trigger && trigger.getAttribute('data-nav-panel-trigger');
    if (id) {
      const match = panels.find((panel) => panel.getAttribute('data-nav-panel') === id);
      if (match) return match;
    }
    return panels[0];
  }

  function positionPanel(panel) {
    const header = document.querySelector('#SiteHeader');
    if (!header) return;
    const bounds = header.getBoundingClientRect();
    panel.style.setProperty('--nav-panel-top', `${Math.max(0, bounds.bottom)}px`);
  }

  function announcementBar() {
    return document.querySelector('.custom-announcement-bar');
  }

  function syncAnnouncementOffset() {
    const bar = announcementBar();
    if (!bar) return;
    document.documentElement.style.setProperty('--announcement-height', `${bar.offsetHeight}px`);
  }

  function isOpen() {
    return current !== null;
  }

  function animateView(panel, viewBack) {
    const isBack = viewBack === true || panel.getAttribute('data-nav-panel') === 'root';
    const viewClass = isBack ? 'is-view-back' : 'is-view-forward';
    panel.classList.remove.apply(panel.classList, VIEW_CLASSES);
    requestAnimationFrame(() => {
      panel.classList.add(viewClass);
    });
  }

  function open(source, moveFocus, panelOverride, viewBack) {
    const panel = panelOverride || panelFor(source);
    if (!panel || current === panel) return;

    cancelHoverOpen();

    const headerTrigger =
      source && source.matches && source.matches(DROPDOWN_TRIGGER_SELECTOR) ? source : null;
    const keptTrigger = headerTrigger || (desktop.matches ? currentTrigger : null);
    const drilled = desktop.matches && drilldownOrigin !== null;

    const outgoing = current;
    if (outgoing) {
      outgoing.classList.add('is-swapping');
      panel.classList.add('is-swapping');
      close(undefined, true);
      requestAnimationFrame(() => {
        outgoing.classList.remove('is-swapping');
        panel.classList.remove('is-swapping');
      });
    }

    current = panel;
    currentTrigger = keptTrigger;
    panel.classList.toggle('is-drilled', drilled);
    syncTriggerStates();
    panel.classList.add(OPEN_CLASS);
    panel.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(BODY_CLASS);
    syncAnnouncementOffset();
    positionPanel(panel);
    requestAnimationFrame(() => {
      if (current !== panel) return;
      syncAnnouncementOffset();
      positionPanel(panel);
    });

    if (outgoing) animateView(panel, viewBack);

    if (!moveFocus) return;
    lastFocused = source || document.activeElement;
    const firstFocusable =
      panel.querySelector('.js-nav-panel-close') || panel.querySelector('a[href], button');
    if (firstFocusable) firstFocusable.focus();
  }

  function close(restoreFocus, isSwap) {
    if (!current) return;

    cancelHoverOpen();

    current.classList.remove(OPEN_CLASS);
    current.classList.remove('is-drilled');
    current.classList.remove.apply(current.classList, VIEW_CLASSES);
    current.setAttribute('aria-hidden', 'true');
    current = null;
    currentTrigger = null;
    if (!isSwap) drilldownOrigin = null;
    syncTriggerStates();
    document.documentElement.classList.remove(BODY_CLASS);
    if (!isSwap) syncAnnouncementOffset();

    if (restoreFocus && lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    } else {
      const active = document.activeElement;
      if (
        active &&
        active.closest &&
        active.closest('.header-wrapper') &&
        !active.closest('shopify-account')
      ) {
        active.blur();
      }
    }
    lastFocused = null;
  }

  function cancelHoverClose() {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
  }

  function scheduleHoverClose() {
    cancelHoverClose();
    hoverTimer = setTimeout(() => close(false), HOVER_CLOSE_DELAY);
  }

  function cancelHoverOpen() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
  }

  function scheduleHoverOpen(source) {
    cancelHoverOpen();
    openTimer = setTimeout(() => open(source, false), HOVER_OPEN_DELAY);
  }

  function isHeadingToPanel(event) {
    if (!current) return false;
    const panel = current.getBoundingClientRect();
    const header = document.querySelector('#SiteHeader');
    const top = header ? header.getBoundingClientRect().top : 0;
    return (
      event.clientY >= top &&
      event.clientY <= panel.bottom &&
      event.clientX >= panel.left
    );
  }

  function handleClick(event) {
    const trigger = event.target.closest('[data-nav-panel-trigger], .nav-panel-trigger');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();

      if (trigger.classList.contains('nav-panel-trigger') && isSearchOpen()) {
        document.dispatchEvent(new CustomEvent('predictive-search:close', { bubbles: true }));
      } else if (trigger.classList.contains('nav-panel-trigger') && isOpen()) {
        close(true);
      } else if (
        trigger.classList.contains('nav-panel__row--back') &&
        desktop.matches &&
        drilldownOrigin
      ) {
        const origin = document.querySelector(`[data-nav-panel="${drilldownOrigin}"]`);
        drilldownOrigin = null;
        open(trigger, true, origin, true);
      } else {
        if (trigger.classList.contains('nav-panel__row--drilldown')) {
          const originPanel = trigger.closest('[data-nav-panel]');
          drilldownOrigin = originPanel ? originPanel.getAttribute('data-nav-panel') : null;
        } else if (trigger.matches(DROPDOWN_TRIGGER_SELECTOR)) {
          drilldownOrigin = null;
        }
        open(trigger, true);
      }
      return;
    }

    if (event.target.closest('.js-nav-panel-close')) {
      event.preventDefault();
      close(true);
      return;
    }

    if (isOpen() && !event.target.closest('[data-nav-panel]')) {
      close(false);
    } else {
      syncTriggerStates();
    }
  }

  function handleMouseover(event) {
    if (!desktop.matches) return;

    const trigger = event.target.closest('[data-nav-panel-trigger]');
    if (
      trigger &&
      !trigger.closest('[data-nav-panel]') &&
      (event.target === trigger || trigger.contains(event.target))
    ) {
      cancelHoverClose();
      cancelHoverOpen();
      if (!isOpen()) {
        scheduleHoverOpen(trigger);
      } else {
        open(trigger, false);
      }
      return;
    }

    if (event.target.closest('[data-nav-panel]')) {
      cancelHoverClose();
      cancelHoverOpen();
      return;
    }

    if (isOpen() && isHeadingToPanel(event)) {
      cancelHoverOpen();
      return;
    }

    if (isOpen()) scheduleHoverClose();
  }

  function handleMouseleave(event) {
    if (!desktop.matches) return;

    const megamenuItem = event.target.closest(
      '.site-nav__item.site-nav__expanded-item.site-nav--has-dropdown.site-nav--is-megamenu'
    );

    if (megamenuItem) {
      cancelHoverOpen();
      if (isOpen()) {
        const relatedTarget = event.relatedTarget;
        const isMovingToPanel = relatedTarget && relatedTarget.closest('[data-nav-panel]');

        if (!isMovingToPanel) {
          scheduleHoverClose();
        }
      }
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && isOpen()) close(true);
  }

  function handleResize() {
    if (!current) return;
    syncAnnouncementOffset();
    positionPanel(current);
  }

  function handleScroll() {
    if (current) positionPanel(current);
  }

  function isSearchOpen() {
    return document.documentElement.classList.contains('js-drawer-open--search');
  }

  function handleSearchOpen() {
    if (isOpen()) close(false);
  }

  prepareTriggerLabels();
  syncTriggerStates();
  ariaExpandedObserver = watchVendorAriaExpandedStomps();

  document.addEventListener('click', handleClick);
  document.addEventListener('mouseover', handleMouseover);
  document.addEventListener('mouseleave', handleMouseleave, true);
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('predictive-search:open', handleSearchOpen);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll, { passive: true });

  window.csHeaderNavPanelState = {
    close,
    handlers: {
      click: handleClick,
      mouseover: handleMouseover,
      mouseleave: handleMouseleave,
      keydown: handleKeydown,
      searchOpen: handleSearchOpen,
      resize: handleResize,
      scroll: handleScroll,
    },
    disconnectAriaObserver() {
      if (ariaExpandedObserver) {
        ariaExpandedObserver.disconnect();
        ariaExpandedObserver = null;
      }
    },
  };
}

function teardownHeaderNavPanel() {
  const state = window.csHeaderNavPanelState;
  if (!state) return;

  state.close(false);
  state.disconnectAriaObserver();
  document.removeEventListener('click', state.handlers.click);
  document.removeEventListener('mouseover', state.handlers.mouseover);
  document.removeEventListener('mouseleave', state.handlers.mouseleave, true);
  document.removeEventListener('keydown', state.handlers.keydown);
  document.removeEventListener('predictive-search:open', state.handlers.searchOpen);
  window.removeEventListener('resize', state.handlers.resize);
  window.removeEventListener('scroll', state.handlers.scroll);

  document.documentElement.classList.remove('nav-panel-open');
  window.csHeaderNavPanelState = null;
}

function sectionHasNavPanel(target) {
  return Boolean(target && target.querySelector && target.querySelector('[data-nav-panel]'));
}

document.addEventListener('shopify:section:load', (event) => {
  if (sectionHasNavPanel(event.target)) initHeaderNavPanel();
});

document.addEventListener('shopify:section:unload', (event) => {
  if (sectionHasNavPanel(event.target)) teardownHeaderNavPanel();
});

initHeaderNavPanel();
